import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Col, Form as FormAntd, message, Spin, Table, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Card } from '@hdi-seguros/web-components.card';
import { Modal } from '@hdi-seguros/web-components.modal';

import {
  DatePickerForm,
  Form,
  InputForm,
  SelectForm,
} from '../../../../../../../../../../../../../../../shared/components/Form';

import { ValidateForm } from '../../../../../../../../../../../../../../../shared/utils';

import { GenericDataFormContext } from '../../../../../../../../../../../shared/contexts';
import { StepFourFormContext } from '../../../../../../../../contexts/StepFourForm';
import { WizardDataFormContext } from '../../../../../../../../../../Wizard/contexts/WizardDataForm';

import { COLORS } from '../../../../../../../../../../../../../../../config/themes/colors';
import {
  ColSize,
  RowSize,
} from '../../../../../../../../../../../../../../../shared/components';

import './index.css';
import { TableActivityItem } from '../../../../../../../../../StepTwo/contexts/StepTwoForm';

export interface Person {
  key: string;
  fullName: string;
  documentType: string;
  documentTypeValue: number;
  documentNumber: string;
  cuil: string;
  birthdayDate: string;
  birthdayDateValue: string;
  nationality: string;
  nationalityValue: number;
  gender: string;
  genderValue: number;
}
interface ActivitiesProps {
  activity: TableActivityItem;
  index: number;

  onChangeActivitiesForm: (
    activityKey: string,
    peopleByActivity: Array<any>
  ) => void;
}

export const FormActivities: FC<ActivitiesProps> = ({
  activity,
  index,
  onChangeActivitiesForm,
}) => {
  const validateForm = new ValidateForm();

  const [personForm] = FormAntd.useForm();

  const {
    ageRangeList,
    isLoadingAgeRangeList,
    isLoadingRelationshipList,
    relationshipList,
    getAgeRangeMinMaxById,
  } = useContext(WizardDataFormContext);

  const {
    documentTypeList,
    genderList,
    isLoadingDocumentTypeList,
    isLoadingGenderList,
    isLoadingNationalityList,
    nationalityList,
  } = useContext(GenericDataFormContext);
  const { activitiesSectionForm } = useContext(StepFourFormContext);

  const [canShowPersonModal, setCanShowPersonModal] = useState<boolean>(false);

  const [peopleByActivity, setPeopleByActivity] = useState<Array<Person>>(
    activity?.people ?? []
  );

  const setValuesForm = () => {
    activitiesSectionForm.setFieldValue(
      `activityQuantity${activity.key}`,
      activity.quantity
    );
    activitiesSectionForm.setFieldValue(
      `activityAgeRange${activity.key}`,
      activity.ageRange
    );
    activitiesSectionForm.setFieldValue(
      `activityRelationship${activity.key}`,
      activity.relationship
    );
  };

  const onDeletePerson = (component: string) => {
    const filteredPeople = peopleByActivity.filter(
      (element) => element.key !== component
    );

    setPeopleByActivity(filteredPeople);
    onChangeActivitiesForm(activity.key, filteredPeople);
  };

  const handlerValidateAddPerson = () => {
    if (peopleByActivity.length < Number(activity?.quantity)) {
      setCanShowPersonModal(true);

      return;
    }

    message.error(
      'Se ha alcanzado el número máximo de personas para esta actividad.'
    );
  };

  const MOCK_TABLE_COLUMNS_HEADER = useMemo(
    () => [
      {
        title: 'Apellido y Nombre',
        dataIndex: 'fullName',
      },
      {
        title: 'Tipo de Documento',
        dataIndex: 'documentType',
      },
      {
        title: 'Nro. Documento',
        dataIndex: 'documentNumber',
      },
      {
        title: 'CUIL',
        dataIndex: 'cuil',
      },
      {
        title: 'F. de Nacimiento',
        dataIndex: 'birthdayDate',
      },
      {
        title: 'Nacionalidad',
        dataIndex: 'nationality',
      },
      {
        title: 'Género',
        dataIndex: 'gender',
      },
      {
        title: 'Acción',
        dataIndex: 'action',
        className: 'action-column',
        render: (_: any, data: Person) => (
          <ActionButton
            label=''
            icon={<DeleteOutlined />}
            color='danger'
            type='secondary'
            onClick={() => onDeletePerson(data.key)}
          />
        ),
      },
    ],
    [onDeletePerson]
  );

  const onCancelPersonModal = () => {
    personForm.resetFields();
    setCanShowPersonModal(false);
  };

  const onAddNewPerson = useCallback(async () => {
    try {
      await personForm.validateFields();

      const formFields = personForm.getFieldsValue();

      const updatedBirthdayDate = dayjs(formFields.birthdayDate).format(
        'YYYYMMDD'
      );

      const nationalityOption =
        formFields.nationality !== undefined &&
        formFields.nationality !== null &&
        formFields.nationality !== ''
          ? nationalityList.find(
              (element) => element.value === formFields.nationality
            )
          : undefined;
      const nationalityDescription: string = nationalityOption?.label ?? '';

      const documentTypeDescription: string =
        documentTypeList?.find(
          (element) => element.value === formFields.documentType
        )?.label ?? String(formFields.documentType);

      const genderDescription: string =
        genderList.find((element) => element.value === formFields.gender)
          ?.label ?? String(formFields.gender);

      const newPerson: Person = {
        key: `${Date.now()}-${Math.random()}`,
        fullName: formFields.fullName,
        documentType: documentTypeDescription,
        documentTypeValue: Number(formFields.documentType),
        documentNumber: formFields.documentNumber,
        cuil: formFields.cuil ?? '',
        birthdayDate: dayjs(formFields.birthdayDate).format('DD-MM-YYYY'),
        birthdayDateValue: updatedBirthdayDate,
        nationality: nationalityDescription,
        nationalityValue: nationalityOption ? Number(nationalityOption.value) : 0,
        gender: genderDescription,
        genderValue: Number(formFields.gender),
      };

      setPeopleByActivity((prevValues) => {
        const updatedPeople = [...prevValues, newPerson];
        onChangeActivitiesForm(activity.key, updatedPeople);

        return updatedPeople;
      });

      setCanShowPersonModal(false);
      personForm.resetFields();
    } catch (error) {}
  }, [
    activity.activity,
    activity.relationship,
    documentTypeList,
    genderList,
    nationalityList,
    onChangeActivitiesForm,
    personForm,
  ]);

  useEffect(() => {
    setValuesForm();
  }, []);

  const getDateLimits = useMemo(() => {
    if (!activity?.ageRange) {
      return {
        minDate: undefined,
        maxDate: dayjs().subtract(1, 'day'),
      };
    }

    const ageRangeData = getAgeRangeMinMaxById(activity.ageRange);
    
    if (!ageRangeData) {
      return {
        minDate: undefined,
        maxDate: dayjs().subtract(1, 'day'),
      };
    }

    const edadMinima = parseInt(ageRangeData.edadMinima, 10);
    const edadMaxima = parseInt(ageRangeData.edadMaxima, 10);

    const maxDate = dayjs().subtract(edadMinima, 'year');
    const minDate = dayjs().subtract(edadMaxima, 'year');
    
    const finalMaxDate = maxDate.isAfter(dayjs().subtract(1, 'day')) 
      ? dayjs().subtract(1, 'day') 
      : maxDate;
    
    const finalMinDate = minDate.isAfter(finalMaxDate) ? undefined : minDate;

    return {
      minDate: finalMinDate,
      maxDate: finalMaxDate,
    };
  }, [activity?.ageRange, getAgeRangeMinMaxById]);

  const validateBirthdayDateRange = useCallback((_: any, value: any) => {
    if (!value) {
      return Promise.resolve();
    }

    if (!activity?.ageRange) {
      return Promise.resolve();
    }

    const selectedDate = dayjs(value);
    
    if (!selectedDate.isValid()) {
      return Promise.resolve();
    }

    const { minDate, maxDate } = getDateLimits;

    if (minDate && selectedDate.isBefore(minDate, 'day')) {
      const ageRangeData = getAgeRangeMinMaxById(activity.ageRange);
      if (ageRangeData) {
        return Promise.reject(
          new Error(
            `La fecha de nacimiento no puede ser anterior a ${minDate.format('DD/MM/YYYY')}. La edad máxima permitida es ${ageRangeData.edadMaxima} años.`
          )
        );
      }
      return Promise.reject(
        new Error(
          `La fecha de nacimiento está fuera del rango permitido.`
        )
      );
    }

    if (maxDate && selectedDate.isAfter(maxDate, 'day')) {
      const ageRangeData = getAgeRangeMinMaxById(activity.ageRange);
      if (ageRangeData) {
        return Promise.reject(
          new Error(
            `La fecha de nacimiento no puede ser posterior a ${maxDate.format('DD/MM/YYYY')}. La edad mínima permitida es ${ageRangeData.edadMinima} años.`
          )
        );
      }
      return Promise.reject(
        new Error(
          `La fecha de nacimiento está fuera del rango permitido.`
        )
      );
    }

    return Promise.resolve();
  }, [activity?.ageRange, getDateLimits, getAgeRangeMinMaxById]);

  return (
    <div className='activityForm'>
      <Form
        formProps={{
          form: activitiesSectionForm,
          name: `activity-form-${activity.key}`,
        }}
      >
        <RowSize className='w-100' rowProps={{
          gutter: 0
        }} >
          <Card title=''>
            <RowSize>
              <Col>
                <Typography.Text strong className='font-size-16'>
                  {activity.activityDescription}
                </Typography.Text>
              </Col>
            </RowSize>

            <RowSize className='mt-10'>
              <ColSize size={20}>
                <InputForm
                  formProps={{
                    label: 'Cantidad:',
                    name: `activityQuantity${activity.key}`,
                  }}
                  inputProps={{
                    disabled: true,
                    placeholder: 'Cantidad',
                  }}
                />
              </ColSize>

              <ColSize size={20}>
                <SelectForm
                  formProps={{
                    label: 'Rango de Edad:',
                    name: `activityAgeRange${activity.key}`,
                  }}
                  selectProps={{
                    disabled: true,
                    loading: isLoadingAgeRangeList,
                    options: ageRangeList,
                  }}
                />
              </ColSize>

              <ColSize size={20}>
                <SelectForm
                  formProps={{
                    label: 'Parentesco:',
                    name: `activityRelationship${activity.key}`,
                  }}
                  selectProps={{
                    disabled: true,
                    loading: isLoadingRelationshipList,
                    options: relationshipList,
                  }}
                />
              </ColSize>

              <ColSize size={20}>
                <FormAntd.Item label=' '>
                  <ActionButton
                    className='btn-accent-outline-barbuss'
                    onClick={handlerValidateAddPerson}
                    icon={<PlusOutlined />}
                    label='Agregar Persona'
                    type='secondary'
                    style={{ width: 165, height: 32 }}
                  />
                </FormAntd.Item>
              </ColSize>
            </RowSize>

            <Table
              bordered
              columns={MOCK_TABLE_COLUMNS_HEADER}
              dataSource={peopleByActivity}
              pagination={false}
              className='person-table'
              scroll={{ x: 'auto' }}
            />
          </Card>
        </RowSize>
      </Form>

      <Modal
        centered
        confirmText='Guardar'
        footer
        onCancel={onCancelPersonModal}
        onOk={onAddNewPerson}
        open={canShowPersonModal}
        width={876}
      >
        <Form
          formProps={{
            form: personForm,
            name: `person-form-${activity?.key}`,
          }}
        >
          <Spin spinning={false}>
            <RowSize>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  color: COLORS.secondary[100],
                }}
              >
                Nueva Persona
              </Typography.Title>
            </RowSize>

            <RowSize>
              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <InputForm
                  formProps={{
                    label: 'Apellido y Nombre:',
                    name: 'fullName',
                    rules: validateForm.generateRules({
                      label: 'apellido y nombre',
                      rules: ['required', 'fullName'],
                    }),
                  }}
                  inputProps={{ placeholder: 'Apellido y nombre' }}
                />
              </Col>

              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <SelectForm
                  formProps={{
                    label: 'Tipo de Documento:',
                    name: 'documentType',
                    rules: validateForm.generateRules({
                      label: 'tipo de documento',
                      rules: ['required'],
                    }),
                  }}
                  selectProps={{
                    disabled: !documentTypeList?.length,
                    loading: isLoadingDocumentTypeList,
                    options: documentTypeList,
                  }}
                />
              </Col>

              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <InputForm
                  formProps={{
                    label: 'Número de Documento:',
                    name: 'documentNumber',
                    rules: validateForm.generateRules({
                      label: 'número de documento',
                      rules: ['required', 'number'],
                    }),
                  }}
                  inputProps={{ placeholder: '32478963', maxLength: 8 }}
                />
              </Col>

              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <InputForm
                  formProps={{
                    label: 'CUIL:',
                    name: 'cuil',
                    rules: validateForm.generateRules({
                      label: 'cuil',
                      rules: ['cuil'],
                    }),
                  }}
                  inputProps={{ placeholder: '23324789639' }}
                />
              </Col>

              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <DatePickerForm
                  formProps={{
                    label: 'Fecha de Nacimiento:',
                    name: 'birthdayDate',
                    rules: [
                      ...validateForm.generateRules({
                        label: 'fecha de nacimiento',
                        rules: ['required', 'dateNotAfterToday'],
                      }),
                      {
                        validator: validateBirthdayDateRange,
                      },
                    ],
                  }}
                  datePickerProps={{ 
                    minDate: getDateLimits.minDate,
                    maxDate: getDateLimits.maxDate,
                  }}
                />
              </Col>
              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <SelectForm
                  formProps={{
                    label: 'Nacionalidad:',
                    name: 'nationality',
                    rules: [],
                  }}
                  selectProps={{
                    disabled: nationalityList.length < 1,
                    loading: isLoadingNationalityList,
                    options: nationalityList,
                    showSearch: true,
                  }}
                />
              </Col>
              <Col
                xs={{ span: 12 }}
                sm={{ span: 8 }}
                style={{ paddingRight: 15 }}
              >
                <SelectForm
                  formProps={{
                    label: 'Género:',
                    name: 'gender',
                    rules: validateForm.generateRules({
                      label: 'género',
                      rules: ['required'],
                    }),
                  }}
                  selectProps={{
                    disabled: genderList.length < 1,
                    loading: isLoadingGenderList,
                    options: genderList,
                  }}
                />
              </Col>
            </RowSize>
          </Spin>
        </Form>
      </Modal>
    </div>
  );
};
